import { useState } from "react";
import { useBusiness } from "@/context/BusinessContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase 19 — Public banner shown on unclaimed directory listings.
 *
 * Rendered above the site header on every page of an unclaimed business's
 * storefront. The owner clicks "Claim this listing", types their email
 * (and optionally phone), we POST /api/claim/request and surface the
 * magic-link URL. Once the magic-link is delivered (Phase 19.2 — email +
 * SMS), clicking it flips the business to ``is_claimed=true``.
 */
export default function ClaimListingBanner() {
  const { business } = useBusiness();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  if (!business || business.is_claimed !== false) return null;

  const submit = async (e) => {
    e?.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter an email address");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/claim/request", {
        business_id: business.id,
        email: email.trim(),
        phone: phone.trim(),
      });
      setSubmitted(data);
      toast.success("Claim request received");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Could not submit claim");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        data-testid="claim-banner"
        className="bg-[#C05746] text-white text-sm relative"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            This listing is unclaimed. Are you the owner of {business.business_name}?
          </span>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="claim-banner-cta"
            className="ml-auto underline underline-offset-2 hover:no-underline font-medium"
          >
            Claim this listing
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="claim-modal"
          className="fixed inset-0 z-[70] bg-black/55 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative bg-[#FDFBF7] w-full md:max-w-lg md:rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="claim-modal-close"
              className="absolute top-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-black/5"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="p-6 md:p-8">
              <h2 className="font-serif-display text-2xl md:text-3xl text-[#121614]">
                Claim {business.business_name}
              </h2>
              <p className="mt-2 text-sm text-[#5C625E]">
                Confirm your email (and optionally a mobile number) and we'll send
                a one-time verification link. Once verified, you can manage menus,
                bookings, payments, marketing and customer reviews from your own
                admin area.
              </p>

              {submitted ? (
                <div
                  data-testid="claim-success"
                  className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900 space-y-2"
                >
                  <p className="font-medium">Thanks! We've recorded your claim.</p>
                  <p>
                    Watch your inbox at <code>{email}</code> for the verification
                    link (we're rolling out email delivery + SMS this week).
                  </p>
                  <p className="text-xs">
                    Direct link (for now):{" "}
                    <code className="bg-white/70 px-1.5 py-0.5 rounded font-mono break-all">
                      {submitted.claim_url}
                    </code>
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-3">
                  <label className="block">
                    <span className="text-xs uppercase tracking-wide text-[#5C625E] font-medium">
                      Email
                    </span>
                    <input
                      type="email"
                      required
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="claim-email-input"
                      placeholder="you@yourrestaurant.com.au"
                      className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C05746]/40 focus:border-[#C05746]"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs uppercase tracking-wide text-[#5C625E] font-medium">
                      Mobile (optional, for SMS verification)
                    </span>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      data-testid="claim-phone-input"
                      placeholder="+61 412 345 678"
                      className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C05746]/40 focus:border-[#C05746]"
                    />
                  </label>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setOpen(false)}
                      data-testid="claim-modal-cancel"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={busy}
                      data-testid="claim-modal-submit"
                      className="bg-[#C05746] hover:bg-[#a8442f] text-white"
                    >
                      {busy ? "Submitting…" : "Send verification"}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
